import { TileCoordinate, LODLevel, TileManagerConfig } from '../Types';
import { FACE_MAPPING } from './constants';

/**
 * Pure URL builder for tile coordinates using metadata configuration
 */
export class UrlBuilder {
  /**
   * Build tile URL from coordinate using metadata configuration
   */
  static build(
    coordinate: TileCoordinate,
    metadata: TileManagerConfig['metadata'],
    lodLevels: LODLevel[]
  ): string {
    if (!metadata || !metadata.url) {
      throw new Error('Metadata configuration is required for URL generation');
    }

    // Handle case where no levels are defined (e.g., test images)
    if (!metadata.levels || metadata.levels.length === 0) {
      return metadata.url;
    }

    return this.buildOnlineUrl(coordinate, metadata, lodLevels);
  }

  /**
   * Build online tile URL using metadata format: level_face_row_column.png
   */
  private static buildOnlineUrl(
    coordinate: TileCoordinate,
    metadata: TileManagerConfig['metadata'],
    lodLevels: LODLevel[]
  ): string {
    // Get the base URL without ONLY the image extension (preserves dots in filename like frame_100.0)
    const baseUrl = metadata.url.replace(/\.(jpg|jpeg|png|webp)$/i, '');

    // Map face to full name
    const faceName = FACE_MAPPING[coordinate.face as keyof typeof FACE_MAPPING];
    if (!faceName) {
      throw new Error(`Unknown face: ${coordinate.face}`);
    }

    // Find LOD level configuration
    const lodLevel = lodLevels.find((lod) => lod.level === coordinate.level);
    const tilesPerFace = lodLevel ? lodLevel.tilesPerFace : 1;

    // Validate coordinates against the LOD level bounds
    if (coordinate.x >= tilesPerFace || coordinate.y >= tilesPerFace ||
        coordinate.x < 0 || coordinate.y < 0) {
      console.warn(
        `Tile coordinate out of bounds: (${coordinate.x},${coordinate.y}) for level ${coordinate.level} with ${tilesPerFace}x${tilesPerFace} tiles`
      );
      // Clamp to valid bounds to prevent errors
      coordinate.x = Math.max(0, Math.min(tilesPerFace - 1, coordinate.x));
      coordinate.y = Math.max(0, Math.min(tilesPerFace - 1, coordinate.y));
    }

    // Find the best matching metadata level, ensuring we don't exceed available levels
    let metadataLevel = metadata.levels.find(
      (level) => level.rows_per_face === tilesPerFace && level.cols_per_face === tilesPerFace
    );

    // If no exact match found, find the closest available level that doesn't exceed our requirements
    if (!metadataLevel) {
      // Sort metadata levels by level number
      const sortedLevels = [...metadata.levels].sort((a, b) => a.level - b.level);

      // Find the highest level that doesn't exceed our coordinate level
      for (let i = sortedLevels.length - 1; i >= 0; i--) {
        const level = sortedLevels[i];
        if (level.level <= coordinate.level) {
          metadataLevel = level;
          break;
        }
      }

      // If still no match, use the highest available level
      if (!metadataLevel && sortedLevels.length > 0) {
        metadataLevel = sortedLevels[sortedLevels.length - 1];
        console.warn(`Using max available level ${metadataLevel.level} for requested level ${coordinate.level}`);
      }
    }

    if (!metadataLevel) {
      throw new Error(`No metadata level available for tilesPerFace: ${tilesPerFace}`);
    }

    // Apply coordinate transformations based on face orientation
    const { x: finalX, y: finalY } = this.transformCoordinates(
      coordinate,
      tilesPerFace
    );

    // Double-check coordinates against metadata level bounds
    const maxMetadataX = metadataLevel.cols_per_face - 1;
    const maxMetadataY = metadataLevel.rows_per_face - 1;

    // Ensure coordinates are within valid bounds for both LOD and metadata
    const clampedX = Math.max(0, Math.min(Math.min(tilesPerFace - 1, maxMetadataX), finalX));
    const clampedY = Math.max(0, Math.min(Math.min(tilesPerFace - 1, maxMetadataY), finalY));

    if (clampedX !== finalX || clampedY !== finalY) {
      console.warn(
        `Coordinates clamped from (${finalX},${finalY}) to (${clampedX},${clampedY}) ` +
        `[metadata supports ${metadataLevel.cols_per_face}x${metadataLevel.rows_per_face}]`
      );
    }

    const imageFormatRaw = metadataLevel.image_format;
    const normalizedFormat =
      typeof imageFormatRaw === 'string' ? imageFormatRaw.toLowerCase() : '';
    const fileExtension =
      normalizedFormat === 'png' || normalizedFormat === 'jpg' ? normalizedFormat : 'png';

    // Format: level_face_row_column.<ext> (row=clampedY, column=clampedX)
    return `${baseUrl}_${metadataLevel.level}_${faceName}_${clampedY}_${clampedX}.${fileExtension}`;
  }

  /**
   * Transform coordinates based on face orientation
   */
  private static transformCoordinates(
    coordinate: TileCoordinate,
    tilesPerFace: number
  ): { x: number; y: number } {
    // Ensure input coordinates are within valid bounds first
    const safeX = Math.max(0, Math.min(tilesPerFace - 1, coordinate.x));
    const safeY = Math.max(0, Math.min(tilesPerFace - 1, coordinate.y));

    if (safeX !== coordinate.x || safeY !== coordinate.y) {
      console.warn(`Input coordinates clamped from (${coordinate.x},${coordinate.y}) to (${safeX},${safeY})`);
    }

    let finalX = safeX;
    let finalY = safeY;

    switch (coordinate.face) {
      case 'f': // FRONT face
        finalX = tilesPerFace - 1 - safeX; // Flip X for correct column order
        finalY = safeY;
        break;
      case 'b': // BACK face
        finalX = tilesPerFace - 1 - safeX;
        finalY = safeY;
        break;
      case 'l': // LEFT face
        finalX = safeX;
        finalY = safeY;
        break;
      case 'r': // RIGHT face
        finalX = safeX;
        finalY = safeY;
        break;
      case 'u': // UP face
        finalX = tilesPerFace - 1 - safeX;
        finalY = tilesPerFace - 1 - safeY;
        break;
      case 'd': // DOWN face
        finalX = tilesPerFace - 1 - safeX;
        finalY = tilesPerFace - 1 - safeY;
        break;
      default:
        throw new Error(`Unknown face: ${coordinate.face}`);
    }

    // Additional safety check to ensure final values are never negative
    finalX = Math.max(0, Math.min(tilesPerFace - 1, finalX));
    finalY = Math.max(0, Math.min(tilesPerFace - 1, finalY));

    return { x: finalX, y: finalY };
  }

  /**
   * Generate tile key from coordinate
   */
  static generateTileKey(coordinate: TileCoordinate): string {
    return `${coordinate.face}_L${coordinate.level}_${coordinate.x}_${coordinate.y}`;
  }

  /**
   * Generate spatial key from coordinate (independent of level)
   */
  static generateSpatialKey(coordinate: TileCoordinate): string {
    return `${coordinate.face}_${coordinate.x}_${coordinate.y}`;
  }

  /**
   * Generate spatial key from tile key for cleanup purposes
   */
  static generateSpatialKeyFromTileKey(tileKey: string): string {
    // Parse tile key format: f_L1_0_0 -> face_level_x_y
    const parts = tileKey.split('_');
    if (parts.length !== 4) {
      throw new Error(`Invalid tile key format: ${tileKey}`);
    }

    const face = parts[0];
    const level = parseInt(parts[1].substring(1), 10); // Remove 'L' prefix
    const x = parseInt(parts[2], 10);
    const y = parseInt(parts[3], 10);

    if (isNaN(level) || isNaN(x) || isNaN(y)) {
      throw new Error(`Invalid tile key components: ${tileKey}`);
    }

    // Convert to level 1 spatial coordinates for consistency
    const level1X = Math.floor(x / (1 << (level - 1)));
    const level1Y = Math.floor(y / (1 << (level - 1)));

    return `${face}_${level1X}_${level1Y}`;
  }

  /**
   * Validate tile coordinate
   */
  static validateCoordinate(coordinate: TileCoordinate, lodLevels: LODLevel[]): boolean {
    // Check if face is valid
    if (!(coordinate.face in FACE_MAPPING)) {
      return false;
    }

    // Check if level exists in LOD configuration
    const lodLevel = lodLevels.find((lod) => lod.level === coordinate.level);
    if (!lodLevel) {
      return false;
    }

    // Check if coordinates are within bounds
    const tilesPerFace = lodLevel.tilesPerFace;
    return (
      coordinate.x >= 0 &&
      coordinate.x < tilesPerFace &&
      coordinate.y >= 0 &&
      coordinate.y < tilesPerFace
    );
  }

  /**
   * Get all possible coordinates for a given LOD level
   */
  static getAllCoordinatesForLevel(level: number, lodLevels: LODLevel[]): TileCoordinate[] {
    const lodLevel = lodLevels.find((lod) => lod.level === level);
    if (!lodLevel) {
      return [];
    }

    const coordinates: TileCoordinate[] = [];
    const tilesPerFace = lodLevel.tilesPerFace;
    const faces = Object.keys(FACE_MAPPING) as (keyof typeof FACE_MAPPING)[];

    for (const face of faces) {
      for (let x = 0; x < tilesPerFace; x++) {
        for (let y = 0; y < tilesPerFace; y++) {
          coordinates.push({ x, y, face: face as any, level });
        }
      }
    }

    return coordinates;
  }

  /**
   * Get coordinates for a specific face at a given LOD level
   */
  static getCoordinatesForFace(
    face: string,
    level: number,
    lodLevels: LODLevel[]
  ): TileCoordinate[] {
    const lodLevel = lodLevels.find((lod) => lod.level === level);
    if (!lodLevel || !(face in FACE_MAPPING)) {
      return [];
    }

    const coordinates: TileCoordinate[] = [];
    const tilesPerFace = lodLevel.tilesPerFace;

    for (let x = 0; x < tilesPerFace; x++) {
      for (let y = 0; y < tilesPerFace; y++) {
        coordinates.push({ x, y, face: face as any, level });
      }
    }

    return coordinates;
  }
}
